import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { trace } from '@opentelemetry/api';
import { getCLS, getFID, getLCP, getFCP, getTTFB } from 'web-vitals';

// ============================================
// Configuración
// ============================================

const SERVICE_NAME = 'PANACLOUD_FRONTEND';
const TENANT_ID = 'panacea';
const COLLECTOR_URL = 'https://otelcollector-http.unknowngravity.com/v1/traces';

// ============================================
// Exporter y Provider
// ============================================

const exporter = new OTLPTraceExporter({
	url: COLLECTOR_URL,
});

const processor = new BatchSpanProcessor(exporter, {
	maxQueueSize: 2048,
	maxExportBatchSize: 512,
	scheduledDelayMillis: 5000,
	exportTimeoutMillis: 30000,
});

const provider = new WebTracerProvider({
	resource: resourceFromAttributes({
		[ATTR_SERVICE_NAME]: SERVICE_NAME,
		tenant_id: TENANT_ID,
		platform: 'unknowngravity',
	}),
	spanProcessors: [processor],
});

provider.register();

// ============================================
// Flush spans al cerrar/ocultar la página
// Sin esto, los Web Vitals (LCP, CLS) que se
// disparan en visibilitychange se pierden.
// ============================================

function flushSpans() {
	try {
		processor.forceFlush();
	} catch (e) {
		// silenciar errores en flush
	}
}

document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'hidden') {
		flushSpans();
	}
});

window.addEventListener('beforeunload', flushSpans);
window.addEventListener('pagehide', flushSpans);

// ============================================
// Instrumentación automática HTTP
// ============================================

// Ignorar la URL del collector para evitar spans recursivos
// y CORS preflight por headers traceparent/tracestate
const COLLECTOR_IGNORE = /otelcollector.*unknowngravity\.com/;

registerInstrumentations({
	instrumentations: [
		new FetchInstrumentation({
			propagateTraceHeaderCorsUrls: [
				/http:\/\/localhost/,
				/api\.vivepanacea\.com/,
			],
			ignoreUrls: [
				COLLECTOR_IGNORE,
				/google-analytics/,
				/googletagmanager/,
				/facebook/,
				/hotjar/,
			],
		}),
		new XMLHttpRequestInstrumentation({
			propagateTraceHeaderCorsUrls: [
				/http:\/\/localhost/,
				/api\.vivepanacea\.com/,
			],
			ignoreUrls: [COLLECTOR_IGNORE],
		}),
	],
});

// ============================================
// Web Vitals
// ============================================

const vitalsTracer = trace.getTracer('web-vitals');

function reportWebVital(metric) {
	const span = vitalsTracer.startSpan(
		`web_vital_${metric.name.toLowerCase()}`,
		{
			attributes: {
				'web_vital.name': metric.name,
				'web_vital.value': metric.value,
				'web_vital.delta': metric.delta,
				'web_vital.id': metric.id,
				'page.url': window.location.href,
				'page.path': window.location.pathname,
			},
		},
	);
	span.end();
}

getCLS(reportWebVital);
getFID(reportWebVital);
getLCP(reportWebVital);
getFCP(reportWebVital);
getTTFB(reportWebVital);

// ============================================
// Captura de errores JavaScript (CRÍTICO)
// ============================================

const errorTracer = trace.getTracer('client-errors');

function getErrorAttrs(source) {
	return {
		'error.source': source,
		'page.url': window.location.href,
		'page.path': window.location.pathname,
	};
}

// Errores síncronos globales
window.onerror = (message, source, lineno, colno, error) => {
	const span = errorTracer.startSpan('js_error', {
		attributes: {
			...getErrorAttrs('window.onerror'),
			'error.message': String(message),
			'error.source_file': source,
			'error.line': lineno,
			'error.column': colno,
		},
	});
	if (error) {
		span.recordException(error);
		span.setAttribute('error.name', error.name);
	}
	span.setStatus({ code: 2, message: String(message) });
	span.end();
	return false;
};

// Promesas rechazadas no manejadas
window.onunhandledrejection = event => {
	const reason = event.reason;
	const error = reason instanceof Error ? reason : new Error(String(reason));

	const span = errorTracer.startSpan('unhandled_promise_rejection', {
		attributes: {
			...getErrorAttrs('window.onunhandledrejection'),
			'error.reason': String(reason),
		},
	});
	span.recordException(error);
	span.setStatus({ code: 2, message: String(reason) });
	span.end();
};

// ============================================
// Page Load Timing
// ============================================

function capturePageLoad() {
	setTimeout(() => {
		const timing = performance.getEntriesByType('navigation')[0];
		if (!timing) return;

		const span = vitalsTracer.startSpan('page_load', {
			attributes: {
				'page.url': window.location.href,
				'timing.dns_ms': Math.round(
					timing.domainLookupEnd - timing.domainLookupStart,
				),
				'timing.tcp_ms': Math.round(
					timing.connectEnd - timing.connectStart,
				),
				'timing.request_ms': Math.round(
					timing.responseStart - timing.requestStart,
				),
				'timing.response_ms': Math.round(
					timing.responseEnd - timing.responseStart,
				),
				'timing.dom_interactive_ms': Math.round(
					timing.domInteractive - timing.startTime,
				),
				'timing.dom_complete_ms': Math.round(
					timing.domComplete - timing.startTime,
				),
				'timing.load_event_ms': Math.round(
					timing.loadEventEnd - timing.startTime,
				),
			},
		});
		span.end();
	}, 100);
}

if (document.readyState === 'complete') {
	capturePageLoad();
} else {
	window.addEventListener('load', capturePageLoad);
}

// ============================================
// Exports para uso en componentes
// ============================================

/**
 * Reportar errores de React Error Boundaries a SigNoz
 */
export function reportReactError(error, errorInfo = {}) {
	const span = errorTracer.startSpan('react_error_boundary', {
		attributes: {
			...getErrorAttrs('error_boundary'),
			'error.name': error.name,
			'error.message': error.message,
			'error.component_stack': errorInfo.componentStack || null,
		},
	});
	span.recordException(error);
	span.setStatus({ code: 2, message: error.message });
	span.end();
}

/**
 * Crear span manual para medir operaciones de UI
 */
export function startUISpan(name, attributes = {}) {
	return trace.getTracer('ui-operations').startSpan(name, {
		attributes: { 'page.url': window.location.href, ...attributes },
	});
}
